import { CloseOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { paramOpts } from "@src/types/param";
import { Button, Form, FormInstance, Input, InputRef, Select, Typography, Card } from "antd";
import React from "react";

const { Text } = Typography;

interface Props {
    name: number;
    form: FormInstance<object>;
    draftData?: any;
    onDraftChange?: (newData: any) => void;
}

export const AnalyticEventParams: React.FC<Props> = ({ name, form, draftData, onDraftChange }) => {
    // Always call hooks first
    const formEventParams = Form.useWatch(["analytic_events", name, "event_params"], form) || [];

    // Then use draft data if available
    const eventParams = draftData ? draftData.event_params || [] : formEventParams;
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    const handleAddParameter = () => {
        const newParam = {
            param_key: "",
            param_type: "string",
            is_new: true,
        };

        if (draftData && onDraftChange) {
            // Update draft data
            const newEventParams = [...eventParams, newParam];
            onDraftChange({
                ...draftData,
                event_params: newEventParams,
            });
        } else {
            // Fallback to direct form update
            form.setFieldValue(["analytic_events", name, "event_params", eventParams.length], newParam);
        }

        // Auto scroll to bottom after adding new parameter
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: "smooth",
                });
            }
        }, 100);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Text strong>Event Parameters</Text>
                    <Text type="secondary" className="block text-xs mt-1">
                        Define the data fields that will be tracked with this event
                    </Text>
                </div>
                <Button icon={<PlusCircleOutlined />} size="small" type="dashed" onClick={handleAddParameter}>
                    Add Parameter
                </Button>
            </div>

            <div className="space-y-3">
                {eventParams.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 bg-gray-50 rounded border-dashed border-2">
                        <Text type="secondary">No parameters defined yet</Text>
                        <br />
                        <Text type="secondary" className="text-xs">
                            Click "Add Parameter" to start tracking data with this event
                        </Text>
                    </div>
                ) : (
                    <div ref={scrollContainerRef} className="max-h-[400px] overflow-y-auto pr-2 event-params-scroll">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {eventParams.map((param: any, paramIndex: number) => (
                                <EventParam
                                    key={paramIndex}
                                    paramIndex={paramIndex}
                                    param={param}
                                    draftData={draftData}
                                    onDraftChange={onDraftChange}
                                    onRemove={(indexToRemove) => {
                                        if (draftData && onDraftChange) {
                                            const newEventParams = eventParams.filter(
                                                (_: any, i: number) => i !== indexToRemove,
                                            );
                                            onDraftChange({
                                                ...draftData,
                                                event_params: newEventParams,
                                            });
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const EventParam: React.FC<{
    paramIndex: number;
    param: any;
    draftData?: any;
    onDraftChange?: (newData: any) => void;
    onRemove: (index: number) => void;
}> = ({ paramIndex, param, draftData, onDraftChange, onRemove }) => {
    const param_key = param.param_key || "";
    const param_type = param.param_type || "string";
    const is_new = param.is_new || false;
    const [isEditing, setIsEditing] = React.useState(Boolean(is_new));

    React.useEffect(() => {
        setIsEditing(is_new);
    }, [is_new]);

    const editInputRef = React.useRef<InputRef>(null);
    React.useEffect(() => {
        if (isEditing) {
            setTimeout(() => editInputRef.current?.focus(), 100);
        }
    }, [isEditing]);

    const updateParam = (updates: any) => {
        if (draftData && onDraftChange) {
            const newEventParams = [...(draftData.event_params || [])];
            newEventParams[paramIndex] = { ...newEventParams[paramIndex], ...updates };
            onDraftChange({
                ...draftData,
                event_params: newEventParams,
            });
        }
    };

    const handleSave = () => {
        if (param_key?.trim()) {
            setIsEditing(false);
            updateParam({ is_new: false });
        }
    };

    const handleCancel = () => {
        if (is_new && !param_key?.trim()) {
            onRemove(paramIndex);
        } else {
            setIsEditing(false);
        }
    };

    return (
        <Card size="small" className="border border-gray-200 hover:border-blue-300 transition-colors">
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <Text strong className="text-sm">
                        Parameter {paramIndex + 1}
                    </Text>
                    <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => onRemove(paramIndex)}
                        className="text-red-500"
                        title="Remove parameter"
                    />
                </div>

                <div className="space-y-2">
                    <div>
                        <Text className="text-xs text-gray-600 block mb-1">Parameter Name</Text>
                        {isEditing ? (
                            <div className="flex items-center space-x-2">
                                <Input
                                    ref={editInputRef}
                                    placeholder="e.g., level, score, user_id"
                                    size="small"
                                    value={param_key}
                                    onChange={(e) => updateParam({ param_key: e.target.value })}
                                    onPressEnter={handleSave}
                                    className="font-mono flex-1"
                                />
                                <Button type="primary" size="small" onClick={handleSave} className="px-2">
                                    ✓
                                </Button>
                                <Button size="small" onClick={handleCancel} className="px-2">
                                    ✕
                                </Button>
                            </div>
                        ) : (
                            <div
                                className="flex items-center justify-between p-2 bg-gray-50 border rounded cursor-pointer hover:bg-gray-100"
                                onClick={() => setIsEditing(true)}
                            >
                                <code className="text-blue-800 text-sm">{param_key || "Unnamed"}</code>
                                <EditOutlined className="text-gray-400 text-xs" />
                            </div>
                        )}
                    </div>

                    <div>
                        <Text className="text-xs text-gray-600 block mb-1">Data Type</Text>
                        <Select
                            options={paramOpts}
                            size="small"
                            placeholder="Select type"
                            className="w-full"
                            value={param_type}
                            onChange={(value) => updateParam({ param_type: value })}
                        />
                    </div>
                </div>
            </div>
        </Card>
    );
};
